/**
 * 归档会话的渲染口径（14.36；纯函数，可单测）。
 *
 * 归档是纯本地标记（store/archiveState），会话数据仍是同一份 ConversationRecord：
 * 列表渲染时按标记把行分流成「主列表」与「归档」，不复制也不改 store。
 * 旧版把整个 Chat 快照另存一份（legacy §5.7），新版不需要——会话 store 本就
 * 持久化，归档只存键。
 *
 * 入口行口径（旧版 _archivePeekVisible：有归档才显示，legacy chat_list_screen）：
 * - 未读 = 归档会话未读之和（旧版全局角标按筛选视图求和同口径）；
 * - 角标 muted = 未读全部来自免打扰会话（弱化样式不隐藏，conversationRowModel 同口径）；
 * - 预览 = 前 3 个会话名（friendNote 优先，conversationRowView 同口径）顿号连接，
 *   占位行（无名）跳过——不造假名。
 */
import type { ConversationRecord } from '~/worker/contract';
import type { ArchiveSlice } from '~/store/archiveState';

export interface ArchivePartition {
  /** 主列表行（已剔除归档） */
  readonly visible: readonly ConversationRecord[];
  /** 归档行（保持入参的 compareConversations 序） */
  readonly archived: readonly ConversationRecord[];
}

/** 无归档（含未加载）时 visible 返回原引用，archived 为固定空数组 */
export function partitionArchived(
  rows: readonly ConversationRecord[],
  archive: ArchiveSlice,
): ArchivePartition {
  if (archive === null || archive.size === 0) return { visible: rows, archived: EMPTY };
  const archived = rows.filter((r) => archive.has(r.key));
  if (archived.length === 0) return { visible: rows, archived: EMPTY };
  return { visible: rows.filter((r) => !archive.has(r.key)), archived };
}

const EMPTY: readonly ConversationRecord[] = [];

export interface ArchiveEntryView {
  readonly count: number;
  readonly preview: string;
  readonly badge: { readonly text: string; readonly muted: boolean } | null;
}

export function archiveEntryView(archived: readonly ConversationRecord[]): ArchiveEntryView {
  let unread = 0;
  let loudUnread = false;
  const names: string[] = [];
  for (const r of archived) {
    const { remote } = r;
    unread += remote.unread;
    if (remote.unread > 0 && remote.noDisturbing === 0) loudUnread = true;
    const name = remote.friendNote || remote.nickname;
    if (name !== '' && names.length < 3) names.push(name);
  }
  return {
    count: archived.length,
    preview: names.join('、'),
    badge: unread > 0 ? { text: unread > 99 ? '99+' : String(unread), muted: !loudUnread } : null,
  };
}

export type ConversationMenuActionId = 'archive' | 'unarchive';

export interface ConversationMenuAction {
  readonly id: ConversationMenuActionId;
  readonly label: string;
  readonly danger: boolean;
}

/**
 * 会话行菜单动作集（PC 右键 / H5 长按）。14.36 只有归档一项——置顶/免打扰/
 * 删除会话在新版已各有落点（好友详情页 14.21、群设置页 14.25），这里不重复入口。
 */
export function conversationMenuActions(archived: boolean): readonly ConversationMenuAction[] {
  return archived
    ? [{ id: 'unarchive', label: '移出归档', danger: false }]
    : [{ id: 'archive', label: '归档', danger: false }];
}
